import { Link } from "react-router-dom";
import { Check, House } from "lucide-react";
import { gbp, longDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const DONE = ["done", "explained"];

export const PassLabel = ({ children, className }) => (
  <p className={cn("text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground", className)}>{children}</p>
);

const Note = ({ step }) => {
  if (step.status === "later") return <span className="shrink-0 text-[11px] text-muted-foreground">Later</span>;
  if (step.status === "needs_fixing") return <span className="shrink-0 text-[11px] text-warning">Fix</span>;
  return null;
};

const groups = (steps) =>
  steps.reduce((out, s, i) => {
    const last = out[out.length - 1];
    if (last && last.section === s.section) last.steps.push({ ...s, index: i });
    else out.push({ section: s.section, steps: [{ ...s, index: i }] });
    return out;
  }, []);

const Dot = ({ state }) => (
  <span className={cn("relative z-[1] grid size-6 shrink-0 place-items-center rounded-full border-[1.5px] bg-panel",
    state === "done" && "border-foreground bg-foreground text-background",
    state === "current" && "border-foreground",
    state === "upcoming" && "border-muted-foreground/40")}>
    {state === "done" ? <Check className="size-3" strokeWidth={3} /> : state === "current" && <span className="size-2 rounded-full bg-foreground" />}
  </span>
);

/** Desktop only: the property and the whole route, with every step one click away. */
export const JourneyPanel = ({ data, token, index }) => {
  const route = groups(data.steps);
  return (
    <aside className="hidden md:block" data-testid="journey-panel">
      <div className="sticky top-8">
        <PassLabel>{data.let.agency_name} · Rental application</PassLabel>
        <p className="mt-2 text-base font-semibold leading-snug">{data.let.address}</p>
        <p className="mt-1 text-sm text-muted-foreground">{gbp(data.let.monthly_rent)} a month · move in {longDate(data.let.move_in_date)}</p>

        <ol className="relative mt-8">
          <span className="absolute bottom-3 left-[11px] top-3 border-l-[1.5px] border-dotted border-muted-foreground/40" />
          {route.map((g) => {
            const current = g.steps.some((s) => s.index === index);
            const state = current ? "current" : g.steps.every((s) => DONE.includes(s.status) || s.kind === "review") && g.steps[0].index < index ? "done" : "upcoming";
            return (
              <li key={`${g.section}-${g.steps[0].key}`} className="relative pb-5">
                <Link to={`/a/${token}/${g.steps[0].key}`} data-testid={`journey-section-${g.steps[0].key}`} className="flex items-center gap-3">
                  <Dot state={state} />
                  <span className={cn("text-sm transition-colors hover:text-foreground", state === "upcoming" ? "text-muted-foreground" : "font-semibold")}>{g.section}</span>
                  {g.steps.length === 1 && <Note step={g.steps[0]} />}
                </Link>
                {g.steps.length > 1 && <ul className="ml-9 mt-1.5 flex flex-col">
                  {g.steps.map((s) => (
                    <li key={s.key}>
                      <Link to={`/a/${token}/${s.key}`} data-testid={`journey-step-${s.key}`}
                        className={cn("flex items-center gap-2 rounded-md py-1 text-[13px] leading-snug transition-colors hover:text-foreground",
                          s.index === index ? "font-medium text-foreground" : "text-muted-foreground")}>
                        <span className="min-w-0 flex-1">{s.title}</span>
                        {DONE.includes(s.status) && s.kind !== "review" && <Check className="size-3.5 shrink-0 text-success" strokeWidth={2.5} />}
                        <Note step={s} />
                      </Link>
                    </li>
                  ))}
                </ul>}
              </li>
            );
          })}
          <li className="relative flex items-center gap-3">
            <span className="relative z-[1] grid size-6 shrink-0 place-items-center rounded-full bg-brand text-brand-foreground"><House className="size-3.5" /></span>
            <span className="text-sm font-semibold">Your new home</span>
          </li>
        </ol>
      </div>
    </aside>
  );
};
